// aiHelpers.js - AI Helper functions for Vue components
export const aiHelpers = {
    // Format patient data for AI requests
    formatPatientForAI(patient) {
        return {
            patient_id: patient.PatientID || patient.ID,
            name: `${patient.FirstName || ''} ${patient.LastName || ''}`.trim(),
            age: patient.Age || this.calculateAge(patient.DateOfBirth),
            gender: patient.Gender,
            date_of_birth: patient.DateOfBirth
        }
    },

    // Calculate age from date of birth
    calculateAge(dateOfBirth) {
        if (!dateOfBirth) return null
        const today = new Date()
        const birthDate = new Date(dateOfBirth)
        let age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }
        return age
    },

    // Create AI request payload for your API system
    createAIRequest(message, patientId, options = {}) {
        return {
            Message: message,
            PatientId: patientId,
            ReportId: options.reportId,
            ResourceIds: options.resources || [], 
            TemplateId: options.template ? options.template.ReportTemplateID : null,
            CurrentContent: options.currentContent,
            Mode: options.mode || 1,
            GPT_MODEL: options.gpt_model || 5,
            Context: options.context || {}
        }
    },

    // Process AI response for display
    processAIResponse(response) {
        if (!response || !response.Success) {
            return {
                success: false,
                content: response?.Error || 'Failed to generate AI response',
                error: response?.Error
            }
        }

        return {
            success: true,
            content: response.Response,
            insights: response.KeyInsights || [],
            recommendations: response.Recommendations || [],
            riskFactors: response.RiskFactors || [],
            metadata: response.Metadata || {},
            canCopy: true,
            canSave: true
        }
    },

    // Format AI content for CKEditor
    formatForEditor(content) {
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>')
            .replace(/^/, '<p>')
            .replace(/$/, '</p>')
            .replace(/<p><\/p>/g, '')
    },

    // Validate AI request
    validateAIRequest(message, patientId, resources = []) {
        const errors = []

        if (!message?.trim()) {
            errors.push('Message is required')
        }

        if (!patientId || patientId <= 0) {
            errors.push('Valid patient ID is required')
        }

        if (message?.length > 5000) {
            errors.push('Message is too long (max 5000 characters)')
        }

        if (resources.length > 10) {
            errors.push('Too many resources selected (max 10)')
        }

        return {
            isValid: errors.length === 0,
            errors
        }
    },

    // Format resources for AI processing
    formatResourcesForAI(resources) {
        return resources.map(resource => ({
            resource_id: resource.ResourceID,
            name: resource.Name,
            type: resource.Type,
            content: resource.Content,
            date_created: resource.DateCreated,
            metadata: resource.Metadata || {}
        }))
    },

    // Format template for AI processing
    formatTemplateForAI(template) {
        if (!template) return null

        return {
            template_id: template.ReportTemplateID,
            name: template.Name,
            description: template.Description,
            sections: template.Sections || [],
            structure: template.Structure || {},
            instructions: template.Instructions || template.Template,
            type: template.Type
        }
    },

    // Sanitize AI content before displaying
    sanitizeAIContent(content) {
        if (!content) return ''

        // Basic HTML sanitization - you can enhance this as needed
        return content
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
            .replace(/javascript:/gi, '')
            .replace(/on\w+\s*=/gi, '')
    },

    // Extract insights from AI response
    extractInsights(response) {
        const insights = []

        if (response.KeyInsights && Array.isArray(response.KeyInsights)) {
            insights.push(...response.KeyInsights)
        }

        if (response.RiskFactors && Array.isArray(response.RiskFactors)) {
            insights.push(...response.RiskFactors.map(risk => `Risk: ${risk}`))
        }

        if (response.Recommendations && Array.isArray(response.Recommendations)) {
            insights.push(...response.Recommendations.map(rec => `Recommendation: ${rec}`))
        }

        return insights
    },

    // Check if AI response contains actionable items
    hasActionableItems(response) {
        return !!(
            (response.Recommendations && response.Recommendations.length > 0) ||
            (response.RiskFactors && response.RiskFactors.length > 0) ||
            (response.KeyInsights && response.KeyInsights.length > 0)
        )
    },

    // Generate summary of AI interaction
    generateInteractionSummary(userMessage, aiResponse, metadata = {}) {
        return {
            timestamp: new Date().toISOString(),
            userQuery: userMessage,
            aiResponseLength: aiResponse.length,
            hasInsights: this.hasActionableItems({ KeyInsights: metadata.insights }),
            hasRecommendations: this.hasActionableItems({ Recommendations: metadata.recommendations }),
            hasRiskFactors: this.hasActionableItems({ RiskFactors: metadata.riskFactors }),
            requestType: metadata.requestType || 'general',
            processingTime: metadata.processingTime || null
        }
    }
}

export default aiHelpers;